import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

const TOOLTIP_MAX_WIDTH = 280;
const VIEWPORT_GAP = 12;

const AdminFieldHelp = ({ help, helpExample }) => {
  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: 'top', ready: false });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;

    const rect = trigger.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();
    const width = bubbleRect.width || Math.min(TOOLTIP_MAX_WIDTH, window.innerWidth - VIEWPORT_GAP * 2);
    const height = bubbleRect.height || 40;

    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(VIEWPORT_GAP, Math.min(left, window.innerWidth - width - VIEWPORT_GAP));

    const spaceAbove = rect.top - VIEWPORT_GAP;
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_GAP;
    const preferTop = spaceAbove >= height + 8 || spaceAbove >= spaceBelow;

    let top;
    let placement;
    if (preferTop) {
      top = rect.top - height - 8;
      placement = 'top';
      if (top < VIEWPORT_GAP) {
        top = rect.bottom + 8;
        placement = 'bottom';
      }
    } else {
      top = rect.bottom + 8;
      placement = 'bottom';
      if (top + height > window.innerHeight - VIEWPORT_GAP) {
        top = Math.max(VIEWPORT_GAP, rect.top - height - 8);
        placement = 'top';
      }
    }

    setCoords({ top, left, placement, ready: true });
  }, []);

  useEffect(() => {
    if (!open) {
      setCoords((prev) => ({ ...prev, ready: false }));
      return undefined;
    }

    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);

    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, updatePosition, help, helpExample]);

  if (!help && !helpExample) return null;

  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  return (
    <span
      ref={triggerRef}
      className={`admin-field-help${open ? ' is-open' : ''}`}
      tabIndex={0}
      aria-label="Aide"
      aria-describedby={open ? tooltipId : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <Icon icon="mdi:information-outline" className="admin-field-help__icon" aria-hidden />
      {open && createPortal(
        <span
          ref={bubbleRef}
          id={tooltipId}
          className={`admin-field-help__bubble admin-field-help__bubble--portal is-${coords.placement}${coords.ready ? ' is-ready' : ''}`}
          role="tooltip"
          style={{ top: coords.top, left: coords.left }}
        >
          {help && <span>{help}</span>}
          {helpExample && (
            <span className="admin-field-help__example">
              {help && <br />}
              <strong>Exemple :</strong>
              {' '}
              {helpExample}
            </span>
          )}
        </span>,
        document.body,
      )}
    </span>
  );
};

export default AdminFieldHelp;
